import React, { Children, cloneElement, isValidElement, useRef, useEffect, useCallback, useState } from 'react';
import styled from '@emotion/styled';
import throttle from 'lodash/throttle';

type Props = {
  children: Node,
  scrollToActive: boolean,
  className?: string,
  selectedIndex?: number,
  onClick?: Function,
  styles?: Object,
  schemeMode?: 'dark' | 'light',
  mode?: 'Meal' | 'MealDealCategories',
  type?: 'line' | 'card',
  lastElSticky?: boolean,
};

export const TabList = ({
  children,
  className,
  onClick,
  selectedIndex,
  styles,
  schemeMode,
  mode,
  type,
  lastElSticky,
  scrollToActive = true,
}: Props) => {
  const activeMenuRef = useRef(null);
  const wrapperRef = useRef(null);
  const [scrolledToRight, setScrolledToRight] = useState(false);

  useEffect(() => {
    if (activeMenuRef.current && scrollToActive) {
      activeMenuRef.current.scrollIntoView({ inline: 'center' });
    }
  }, [selectedIndex, activeMenuRef, scrollToActive]);

  const addShadowToLastTab = useCallback(
    throttle(() => {
      if (wrapperRef.current) {
        const { scrollWidth, scrollLeft, clientWidth } = wrapperRef.current;
        setScrolledToRight(scrollWidth - scrollLeft > clientWidth);
      }
    }, 200),
    [wrapperRef]
  );

  useEffect(() => {
    if (wrapperRef.current) {
      addShadowToLastTab();
      wrapperRef.current.addEventListener('scroll', addShadowToLastTab);
    }
    return () => wrapperRef.current.removeEventListener('scroll', addShadowToLastTab);
  }, [wrapperRef, addShadowToLastTab]);

  return (
    <StyledTabList
      ref={wrapperRef}
      css={styles}
      className={className}
      role="tablist"
      type={type}
      scrolledToRight={scrolledToRight}
      lastElSticky={lastElSticky}
    >
      {Children.toArray(children)
        .filter(isValidElement)
        .map((child, index, validElements) => {
          return cloneElement(child, {
            onClick: () => onClick(index),
            isSelected: index === selectedIndex,
            schemeMode,
            mode,
            type,
            elemRef: activeMenuRef,
            lastElSticky,
            index: index + 1,
            totalTabs: validElements.length,
          });
        })}
    </StyledTabList>
  );
};

TabList.tabsRole = 'TabList';

const StyledTabList = styled('div')`
  display: flex;
  padding: 0;
  margin: 0 0 24px;

  ${({ type, theme, scrolledToRight, lastElSticky }) =>
    type !== 'card' &&
    `
    white-space: nowrap;
    position: relative;
    width: 100vw;
    overflow-x: auto;
    background: ${theme.color.white};
    -webkit-overflow-scrolling: touch;
    margin: ${theme.constants.headerHeight} 0 0;
    box-shadow: ${theme.boxShadow.small};

    @media (${theme.breakpoint.desktop}) {
      margin: 0;
    };

    @media (${theme.breakpoint.mobile}) {
      -ms-overflow-style: none;
      scrollbar-width: none;
      margin: 64px 0 0;

      &::-webkit-scrollbar {
        display: none;
      }
    };

    ${
      scrolledToRight &&
      lastElSticky &&
      `
      li:last-child {
        box-shadow: ${theme.boxShadow.large};
      };
    `
    }
`}
`;
